/**
 * QOVO — GA4 ecommerce dataLayer emitter
 *
 * Feeds the GTM container (GTM-KHBGFG32) with the standard GA4 ecommerce
 * events for every STOREFRONT interaction. Purchase is not here: it fires
 * from the Shopify custom web pixel, because GTM cannot run on the checkout
 * or thank-you page on a non-Plus plan.
 *
 * Design note: cart events are captured by intercepting Shopify's own Ajax
 * cart endpoints (/cart/add.js, /cart/change.js, /cart/update.js) rather than
 * by editing product-form.js, quick-add.js, cart-drawer and friends. One file,
 * no theme surgery, and it keeps working if the theme is updated.
 *
 * Depends on window.__QOVO_DL, injected by snippets/qovo-datalayer-config.liquid.
 */

(function () {
  "use strict";

  var CFG = window.__QOVO_DL || {};
  var CURRENCY = CFG.currency || "GBP";
  var dl = (window.dataLayer = window.dataLayer || []);

  // Shopify Ajax API returns money in minor units (pence).
  function money(cents) {
    return Math.round(Number(cents) || 0) / 100;
  }

  function push(eventName, ecommerce) {
    // Clear the previous ecommerce object so GA4 never merges two events.
    dl.push({ ecommerce: null });
    dl.push({ event: eventName, ecommerce: ecommerce });
  }

  // Map a Shopify Ajax cart line item to a GA4 item.
  function lineToItem(line, index) {
    var item = {
      item_id: String(line.sku || line.variant_id || line.id || ""),
      item_name: line.product_title || line.title || "",
      price: money(line.final_price != null ? line.final_price : line.price),
      quantity: Number(line.quantity) || 1,
      currency: CURRENCY
    };
    if (line.vendor) item.item_brand = line.vendor;
    if (line.product_type) item.item_category = line.product_type;
    if (line.variant_title && line.variant_title !== "Default Title") {
      item.item_variant = line.variant_title;
    }
    if (typeof index === "number") item.index = index;
    return item;
  }

  // Snapshot of the cart as we last saw it, so a cart mutation can be diffed
  // into an add or a remove. Declared before the listeners that read it.
  var cartCache = CFG.cart || null;

  // ---------------------------------------------------------------------
  // 1. view_item — product pages
  // ---------------------------------------------------------------------
  if (CFG.template === "product" && CFG.product) {
    push("view_item", {
      currency: CURRENCY,
      value: CFG.product.price,
      items: [CFG.product]
    });
  }

  // ---------------------------------------------------------------------
  // 2. view_item_list + select_item — collection and search pages
  // ---------------------------------------------------------------------
  if (CFG.itemList && CFG.itemList.items && CFG.itemList.items.length) {
    push("view_item_list", {
      item_list_id: CFG.itemList.id,
      item_list_name: CFG.itemList.name,
      items: CFG.itemList.items
    });

    document.addEventListener(
      "click",
      function (e) {
        var link = e.target && e.target.closest ? e.target.closest("a[href*='/products/']") : null;
        if (!link) return;

        // Match the clicked link back to a product in the rendered list by handle.
        var m = link.getAttribute("href").match(/\/products\/([^/?#]+)/);
        if (!m) return;
        var handle = m[1];

        var match = CFG.itemList.items.filter(function (it) {
          return it._handle === handle;
        })[0];
        if (!match) return;

        push("select_item", {
          item_list_id: CFG.itemList.id,
          item_list_name: CFG.itemList.name,
          items: [match]
        });
      },
      true
    );
  }

  // ---------------------------------------------------------------------
  // 3. search
  // ---------------------------------------------------------------------
  // Three paths, because this theme has predictive search enabled and that
  // never navigates to /search. Relying on the Liquid search page alone meant
  // the event never fired for anyone using the dropdown.
  //   a) a real /search results page load
  //   b) submitting a search form (Enter, or the magnifier button)
  //   c) typing in the predictive search box, debounced
  // De-duplicated on the term so one search does not emit three events.
  var lastSearchTerm = null;

  function pushSearch(term) {
    if (!term) return;
    term = String(term).trim();
    if (term.length < 2 || term === lastSearchTerm) return;
    lastSearchTerm = term;
    dl.push({ event: "search", search_term: term });
  }

  // a) real search results page
  if (CFG.searchTerm) pushSearch(CFG.searchTerm);

  // b) form submit
  document.addEventListener(
    "submit",
    function (e) {
      var form = e.target;
      if (!form || !form.getAttribute) return;
      var action = form.getAttribute("action") || "";
      if (action.indexOf("/search") === -1) return;
      var input = form.querySelector("input[name='q']");
      if (input) pushSearch(input.value);
    },
    true
  );

  // c) predictive search typing, debounced so we do not emit per keystroke
  var searchTimer = null;
  document.addEventListener(
    "input",
    function (e) {
      var input = e.target;
      if (!input || input.name !== "q") return;
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        pushSearch(input.value);
      }, 1200);
    },
    true
  );

  // ---------------------------------------------------------------------
  // 4. view_cart
  // ---------------------------------------------------------------------
  if (CFG.template === "cart" && CFG.cart && CFG.cart.items && CFG.cart.items.length) {
    push("view_cart", {
      currency: CURRENCY,
      value: money(CFG.cart.total_price),
      items: CFG.cart.items.map(lineToItem)
    });
  }

  // ---------------------------------------------------------------------
  // 5. begin_checkout — any control that submits to /checkout
  // ---------------------------------------------------------------------
  document.addEventListener(
    "click",
    function (e) {
      var el =
        e.target && e.target.closest
          ? e.target.closest("[name='checkout'], a[href^='/checkout'], .cart__checkout-button")
          : null;
      if (!el) return;
      if (!cartCache || !cartCache.items || !cartCache.items.length) return;

      push("begin_checkout", {
        currency: CURRENCY,
        value: money(cartCache.total_price),
        items: cartCache.items.map(lineToItem)
      });
    },
    true
  );

  // ---------------------------------------------------------------------
  // 6. add_to_cart / remove_from_cart — intercept Shopify's Ajax cart
  // ---------------------------------------------------------------------
  function indexByKey(cart) {
    var map = {};
    ((cart && cart.items) || []).forEach(function (line) {
      map[line.key || line.variant_id] = line;
    });
    return map;
  }

  function diffAndPush(newCart) {
    if (!newCart || !newCart.items) return;

    var before = indexByKey(cartCache);
    var after = indexByKey(newCart);
    var added = [];
    var removed = [];

    Object.keys(after).forEach(function (k) {
      var oldQty = before[k] ? before[k].quantity : 0;
      var delta = after[k].quantity - oldQty;
      if (delta > 0) {
        var a = lineToItem(after[k]);
        a.quantity = delta;
        added.push(a);
      } else if (delta < 0) {
        var r = lineToItem(after[k]);
        r.quantity = -delta;
        removed.push(r);
      }
    });

    // Lines that disappeared entirely.
    Object.keys(before).forEach(function (k) {
      if (after[k]) return;
      removed.push(lineToItem(before[k]));
    });

    function sum(items) {
      return items.reduce(function (t, i) {
        return t + i.price * i.quantity;
      }, 0);
    }

    if (added.length) {
      push("add_to_cart", { currency: CURRENCY, value: sum(added), items: added });
    }
    if (removed.length) {
      push("remove_from_cart", { currency: CURRENCY, value: sum(removed), items: removed });
    }

    cartCache = newCart;
  }

  function refreshCartThenDiff() {
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(diffAndPush)
      .catch(function () { /* non-fatal, tracking only */ });
  }

  function isCartMutation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?/.test(String(url));
  }

  // fetch()
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var isMutation = isCartMutation(url);
      return origFetch.apply(this, arguments).then(function (res) {
        // /cart/add.js returns the added line, not the cart, so always
        // re-read /cart.js and diff. One extra request per cart action.
        if (isMutation && res.ok) refreshCartThenDiff();
        return res;
      });
    };
  }

  // XMLHttpRequest, for themes and apps still using jQuery.ajax
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__qovoCartMutation = isCartMutation(url);
    return origOpen.apply(this, arguments);
  };
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (xhr.__qovoCartMutation) {
      xhr.addEventListener("load", function () {
        if (xhr.status >= 200 && xhr.status < 300) refreshCartThenDiff();
      });
    }
    return origSend.apply(this, arguments);
  };
})();
